import { FileText, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import {
	useContracts,
	useCreateContract,
	useDeleteContract,
	useUpdateContract,
} from '@/api/queries/contracts-queries'
import { useOrganization } from '@/api/queries/organization-queries'
import { ContractFormContent } from '@/components/administration'
import {
	AdminCardPage,
	CardListItem,
	ResponsiveFormModal,
	SearchInput,
} from '@/components/commons'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { adminBackRoute } from '@/constants/admin-menu'
import { ROUTES } from '@/constants/routes'
import type {
	Contract,
	CreateContractBody,
	UpdateContractBody,
} from '@/types/contracts'
import { getApiErrorMessage } from '@/utils/api-error'

export const ContractsPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()

	const [search, setSearch] = useState('')
	const [formOpen, setFormOpen] = useState(false)
	const [editing, setEditing] = useState<Contract | undefined>(undefined)
	const [deleteTarget, setDeleteTarget] = useState<Contract | undefined>(
		undefined,
	)

	/* ── Data ── */
	const { data: contractsData, isLoading: contractsLoading } = useContracts()
	const { data: organization, isLoading: orgLoading } = useOrganization()

	const createMutation = useCreateContract()
	const updateMutation = useUpdateContract()
	const deleteMutation = useDeleteContract()

	const contracts = contractsData?.items ?? []
	const total = contractsData?.total ?? 0
	const branches = organization ?? []

	/* ── IDs of CRs already linked to any contract ── */
	const usedCrIds = new Set(
		contracts.flatMap((contract) => contract.crs.map((cr) => cr.id)),
	)

	/* ── Filtering ── */
	const filtered = search.trim()
		? contracts.filter((c) => {
				const q = search.toLowerCase()
				return (
					c.name.toLowerCase().includes(q) ||
					c.branchName.toLowerCase().includes(q) ||
					c.crs.some(
						(cr) =>
							cr.name.toLowerCase().includes(q) ||
							cr.code.toLowerCase().includes(q),
					)
				)
			})
		: contracts

	/* ── Handlers ── */
	const handleOpenCreate = () => {
		setEditing(undefined)
		setFormOpen(true)
	}

	const handleOpenEdit = (contract: Contract) => {
		setEditing(contract)
		setFormOpen(true)
	}

	const handleSave = async (data: CreateContractBody | UpdateContractBody) => {
		if (editing) {
			try {
				await updateMutation.mutateAsync({
					id: editing.id,
					body: data as UpdateContractBody,
				})
				toast.success(
					t('administration.contracts.editSuccess', { name: editing.name }),
				)
				setFormOpen(false)
				setEditing(undefined)
			} catch (error) {
				toast.error(getApiErrorMessage(error))
			}
		} else {
			try {
				await createMutation.mutateAsync(data as CreateContractBody)
				toast.success(t('administration.contracts.createSuccess'))
				setFormOpen(false)
			} catch (error) {
				toast.error(getApiErrorMessage(error))
			}
		}
	}

	const handleDeleteConfirm = async () => {
		if (!deleteTarget) return
		try {
			await deleteMutation.mutateAsync(deleteTarget.id)
			toast.success(
				t('administration.contracts.deleteSuccess', {
					name: deleteTarget.name,
				}),
			)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
		setDeleteTarget(undefined)
	}

	/* ── Derived state ── */
	const isLoading = contractsLoading || orgLoading
	const formTitle = editing
		? t('administration.contracts.editContract')
		: t('administration.contracts.createNew')

	return (
		<>
			<AdminCardPage
				icon={<FileText className="size-5" />}
				title={t('administration.contracts.title')}
				subtitle={t('administration.contracts.subtitle')}
				onBack={() => navigate(adminBackRoute(ROUTES.ADMINISTRATION.CONTRACTS))}
				footerText={`${total} ${
					total === 1
						? t('administration.contracts.countSingular')
						: t('administration.contracts.countPlural')
				}`}
				actions={
					<Button
						onClick={handleOpenCreate}
						className="gap-2 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90 w-full sm:w-auto"
					>
						<Plus className="size-4" />
						<span>{t('administration.contracts.createNew')}</span>
					</Button>
				}
				modal={
					<ResponsiveFormModal
						open={formOpen}
						onOpenChange={setFormOpen}
						title={formTitle}
						dialogClassName="max-h-[85vh] overflow-y-auto sm:max-w-lg"
					>
						<ContractFormContent
							mode={editing ? 'edit' : 'create'}
							initial={editing}
							branches={branches}
							usedCrIds={usedCrIds}
							onSave={handleSave}
							onClose={() => setFormOpen(false)}
						/>
					</ResponsiveFormModal>
				}
			>
				<SearchInput
					value={search}
					onChange={setSearch}
					placeholder={t('administration.contracts.searchPlaceholder')}
				/>

				{/* List */}
				{isLoading ? (
					<div className="flex items-center justify-center py-16">
						<span className="text-sm text-muted-foreground">
							{t('administration.contracts.loading')}
						</span>
					</div>
				) : filtered.length === 0 ? (
					<div className="flex flex-col items-center gap-2 py-16 text-center text-muted-foreground">
						<FileText className="size-8 text-kaefer-light-silver" />
						<p className="text-sm">{t('administration.contracts.noResults')}</p>
					</div>
				) : (
					<div className="space-y-2">
						{filtered.map((contract) => (
							<CardListItem
								key={contract.id}
								title={contract.name}
								subtitle={contract.branchName}
								badge={
									contract.crs.length > 0
										? `${contract.crs.length} CR${contract.crs.length !== 1 ? 's' : ''}`
										: undefined
								}
								onEdit={() => handleOpenEdit(contract)}
								onDelete={() => setDeleteTarget(contract)}
							/>
						))}
					</div>
				)}
			</AdminCardPage>

			{/* Delete confirmation dialog */}
			<AlertDialog
				open={!!deleteTarget}
				onOpenChange={(open) => !open && setDeleteTarget(undefined)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t('administration.contracts.deleteTitle')}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t('administration.contracts.deleteDescription', {
								name: deleteTarget?.name ?? '',
							})}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>
							{t('administration.contracts.deleteCancelButton')}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleDeleteConfirm}
							className="bg-red-600 text-white hover:bg-red-700"
						>
							{t('administration.contracts.deleteConfirmButton')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
